const Balance = require('../constants/Balance');
const { getAbilityLevel, getBoardDistance } = require('../isomorphic-helpers');

const BattleCalc = {
  adjacent: function (a, b) {
    return BattleCalc.boardDistance(a, b) === 1;
  },

  cavernsRoomIsClear: function (battleState) {
    const all_units_array = Object.values(battleState.allUnits);
    for (const { team, dead, inPlay } of all_units_array) {
      if (team === 'black' && !dead && inPlay) {
        return false;
      }
    }
    return true;
  },

  tileBetweenThemIsOccupied: function (a, b, battleState) {
    if (BattleCalc.adjacent(a, b)) {
      return false;
    }

    var middle_x = (a.x + b.x) / 2;
    var middle_y = (a.y + b.y) / 2;

    var tile = battleState.getTileAtCoords(middle_x, middle_y);
    if (!tile) {
      return false;
    }

    return tile.occupied;
  },

  boardDistance: function (a, b) {
    return getBoardDistance(a, b);
  },

  getAlliesOfUnit: (unit, unitGroup, inclusive = true, include_summons = true) => {
    if (unitGroup && !Array.isArray(unitGroup) && typeof unitGroup === 'object') {
      unitGroup = Object.values(unitGroup);
    }

    unitGroup = unitGroup.filter((u) => !!u).filter((u) => u.inPlay && !u.dead);

    if (!include_summons)
      unitGroup = unitGroup.filter((u) => !['dwarf', 'elf', 'faerie', 'poppet'].includes(u.handle));

    return inclusive
      ? unitGroup.filter((u) => u.team === unit.team)
      : unitGroup.filter((u) => u.team === unit.team && u.uid !== unit.uid);
  },

  getBlessedFactor: function (unit) {
    let blessedFactor = 1.0;
    const blessed = BattleCalc.getCondition(unit, 'blessed');
    if (blessed) {
      blessedFactor -= blessed.potency / 100;
    }
    return blessedFactor;
  },

  getNearestPiece: function (subject, pieces) {
    var nearest = pieces[0];
    var nearestDist = BattleCalc.boardDistance(subject, nearest);
    for (var i = 0; i < pieces.length; ++i) {
      if (BattleCalc.boardDistance(pieces[i], subject) < nearestDist) {
        nearest = pieces[i];
        nearestDist = BattleCalc.boardDistance(subject, nearest);
      }
    }
    return nearest;
  },

  getEquippedAbility: function (unit, abilityHandle) {
    if (!Array.isArray(unit.equipped_abilities) && typeof unit.equipped_abilities === 'object') {
      unit.equipped_abilities = Object.values(unit.equipped_abilities);
    }
    for (const { value } of unit.equipped_abilities) {
      if (value === abilityHandle) {
        if (!Array.isArray(unit.abilityTrees) && typeof unit.abilityTrees === 'object') {
          unit.abilityTrees = Object.values(unit.abilityTrees);
        }
        for (const tree of unit.abilityTrees) {
          if (tree.handle === 'bow_use' && unit?.equipment?.off_hand?.type !== 'bow') {
            continue;
          }

          for (const [handle, ability] of Object.entries(tree.abilities)) {
            if (handle === abilityHandle) {
              return ability;
            }
          }
        }
      }
    }
  },

  getPassiveAbility: function (unit, abilityHandle) {
    if (!Array.isArray(unit.abilityTrees) && typeof unit.abilityTrees === 'object') {
      unit.abilityTrees = Object.values(unit.abilityTrees);
    }
    for (const tree of unit.abilityTrees) {
      for (const [handle, ability] of Object.entries(tree.abilities)) {
        if (handle === abilityHandle && ability.passive) {
          return ability;
        }
      }
    }
  },

  getLeapAPCostByDist: function (unit, dist) {
    const leap = BattleCalc.getEquippedAbility(unit, 'leap');
    if (leap) {
      return (unit.max_ap / leap.range.max) * dist;
    }
    return NaN;
  },

  getMaxNumSummons({ ability_handle, caster_build }) {
    const ability_level = getAbilityLevel({
      ability_handle,
      unit_build: caster_build,
    });
    switch (ability_level) {
      case 1:
        return 2;
      case 2:
        return 3;
      case 3:
        return 4;
      default:
        return 5;
    }
  },

  getTeleportMPCostByDist: function (unit, dist) {
    const teleport = BattleCalc.getEquippedAbility(unit, 'teleport');
    if (teleport) {
      return teleport.mpCost[dist - 1];
    }
    return NaN;
  },

  getTileAtCoords(x, y, candidateTiles) {
    return Object.values(candidateTiles).find((t) => t.x === x && t.y === y);
  },

  getCondition: function (unit, conditionHandle, instigatorId) {
    if (!unit) return null;

    for (const condition of Object.values(unit.conditions) || {})
      if (
        condition.handle === conditionHandle &&
        (!instigatorId || condition.instigatorId === instigatorId)
      )
        return condition;

    return null;
  },

  getAllBleedConditions(unit) {
    return Object.values(unit.conditions).filter((c) => c.handle === 'bleeding');
  },

  getCombinedDoTPotency(unit) {
    const bleeding_conditions = BattleCalc.getAllBleedConditions(unit);
    const poisoned_conditions = Object.values(unit.conditions).filter(
      (c) => c.handle === 'poisoned'
    );
    return [...bleeding_conditions, ...poisoned_conditions].reduce(
      (result, c) => result + c.potency,
      0
    );
  },

  clearDistTwoVictims: function (actor, victims) {
    for (var i = 0; i < victims.length; ++i) {
      if (BattleCalc.boardDistance(actor, victims[i]) === 2) {
        victims.splice(i, 1);
      }
    }

    return victims;
  },

  getAttTotal(unit, attTag) {
    const atts = unit.attributes;
    return (
      atts.base[attTag] +
      atts.fromUpgrades[attTag] +
      atts.fromEquipment[attTag] +
      atts.fromConditions[attTag]
    );
  },

  projectileTime: function (tilesPerSecond, a, b) {
    var dist = BattleCalc.boardDistance(a, b);

    return (dist / tilesPerSecond) * 1000;
  },

  getCooldown: function (unit) {
    return Math.max(0.5, 1.5 - unit.attributes.speed / 30);
  },

  pieceIsBetweenPair: function (subject, pair) {
    if (pair.length !== 2) {
      throw new Error('Invalid vector length in BattleCalc::pieceIsBetweenPairOfPieces()');
    }

    var pairDist = BattleCalc.boardDistance(pair[0], pair[1]);
    var avgOfDistancesFromSubjToNodes =
      (BattleCalc.boardDistance(subject, pair[0]) + BattleCalc.boardDistance(subject, pair[1])) / 2;

    return avgOfDistancesFromSubjToNodes <= pairDist / 2;
  },

  unitHPAbovePercent: function (unit, threshold) {
    return (unit.hp / unit.max_hp) * 100 > threshold;
  },

  unitHPBelowPercent: function (unit, threshold) {
    return (unit.hp / unit.max_hp) * 100 < threshold;
  },

  unitAPAbovePercent(unit, threshold) {
    return (unit.ap / unit.max_ap) * 100 > threshold;
  },

  rollForReflectMagic(defenderUnit) {
    const reflectAbility = BattleCalc.getPassiveAbility(defenderUnit, 'reflect_magic');
    if (Math.random() <= reflectAbility?.potency ?? 0) {
      return true;
    }
    return false;
  },

  isSurrounded(unit, battleState) {
    const adjacentTiles = battleState.getAdjacentTiles(unit);
    if (adjacentTiles.find((t) => !t.occupied)) {
      return false;
    }
    return true;
  },

  targetIsAtBoardEdge(actor, target, battleState) {
    const y_dif = target.y - actor.y;
    const x_dif = target.x - actor.x;
    const tile_behind_target = battleState.getTileAtCoords(target.x + x_dif, target.y + y_dif);
    return !tile_behind_target;
  },

  unitCanUseAbility(actor, ability_handle, victim) {
    const ability = BattleCalc.getEquippedAbility(actor, ability_handle);
    if (!ability) return false;

    const blessedFactor = BattleCalc.getBlessedFactor(actor);
    const apCost =
      (ability.handle === 'leap'
        ? BattleCalc.getLeapAPCostByDist(actor, BattleCalc.boardDistance(actor, victim))
        : ability.apCost) * blessedFactor;
    const mpCost =
      (ability.handle === 'teleport'
        ? BattleCalc.getTeleportMPCostByDist(actor, BattleCalc.boardDistance(actor, victim))
        : ability.mpCost) * blessedFactor;

    if ((!ability.apCost || actor.ap >= apCost) && (!ability.mpCost || actor.mp >= mpCost))
      return true;
    else if (actor.ap < apCost) {
      const adrenaline = BattleCalc.getEquippedAbility(actor, 'adrenaline');
      if (
        adrenaline &&
        !BattleCalc.getCondition(actor, 'scared') &&
        actor.mp >= adrenaline.mpCost * blessedFactor
      )
        return true;
    } else if (actor.mp < mpCost) {
      const mysticFocus = BattleCalc.getEquippedAbility(actor, 'mystic_focus');
      if (mysticFocus && actor.ap >= mysticFocus.apCost * blessedFactor) return true;
    }

    return false;
  },

  unitIsPureRanged(unit) {
    const ai_priorities =
      !Array.isArray(unit.ai.priorities) && typeof unit.ai.priorities === 'object'
        ? Object.values(unit.ai.priorities)
        : unit.ai.priorities;

    const ranged_priority = ai_priorities.findIndex((ele) => ele === 'dps_ranged');
    return (
      ranged_priority < ai_priorities.findIndex((ele) => ele === 'dps_close') &&
      ranged_priority < ai_priorities.findIndex((ele) => ele === 'tank') &&
      (BattleCalc.getEquippedAbility(unit, 'arrow_shot') ||
        BattleCalc.getEquippedAbility(unit, 'void_spike') ||
        BattleCalc.getEquippedAbility(unit, 'multi_shot') ||
        BattleCalc.getEquippedAbility(unit, 'razor_shot') ||
        BattleCalc.getEquippedAbility(unit, 'infect_mind') ||
        BattleCalc.getEquippedAbility(unit, 'molotov_cocktail') ||
        BattleCalc.getEquippedAbility(unit, 'lightning')) &&
      !BattleCalc.getEquippedAbility(unit, 'slash') &&
      !BattleCalc.getEquippedAbility(unit, 'slam') &&
      !BattleCalc.getEquippedAbility(unit, 'taunt') &&
      !BattleCalc.getEquippedAbility(unit, 'armor_break') &&
      !BattleCalc.getEquippedAbility(unit, 'roundhouse') &&
      !BattleCalc.getEquippedAbility(unit, 'mortal_strike') &&
      !BattleCalc.getEquippedAbility(unit, 'bash') &&
      !BattleCalc.getEquippedAbility(unit, 'tussle') &&
      !BattleCalc.getEquippedAbility(unit, 'burning_hands')
    );
  },

  getDamageModFromAffinities(attacker, defender) {
    // get types
    let attacker_types = attacker.hero ? [attacker.hero_handle] : attacker.types;
    let defender_types = defender.hero ? [defender.hero_handle] : defender.types;
    if (!Array.isArray(attacker_types) && typeof attacker_types === 'object')
      attacker_types = Object.values(attacker_types);
    if (!Array.isArray(defender_types) && typeof defender_types === 'object')
      defender_types = Object.values(defender_types);
    // get affinity levels
    let attacker_affinity_level = 0;
    for (const unit_type of defender_types) {
      if ((attacker?.affinities?.attack ?? {})[unit_type]) {
        attacker_affinity_level += attacker.affinities.attack[unit_type];
      }
    }
    let defender_affinity_level = 0;
    for (const unit_type of attacker_types) {
      if ((defender?.affinities?.defense ?? {})[unit_type]) {
        defender_affinity_level += defender.affinities.defense[unit_type];
      }
    }
    // apply affinity adjustments
    const combined_affinity_level = attacker_affinity_level - defender_affinity_level;
    return 1 + combined_affinity_level * Balance.AFFINITY_BONUS_PER_POINT;
  },

  getDamageModFromStances({ instigator, victim }) {
    // get instigator damage mod
    let instigator_dmg_mod = 1.0;
    let ds = BattleCalc.getCondition(instigator, 'defensive_stance');
    let os = BattleCalc.getCondition(instigator, 'offensive_stance');
    if (ds) instigator_dmg_mod = 0.5 + ds.potency;
    else if (os) instigator_dmg_mod = 1.0 + os.potency;

    // get victim damage mod
    let victim_dmg_mod = 1.0;
    ds = BattleCalc.getCondition(victim, 'defensive_stance');
    os = BattleCalc.getCondition(victim, 'offensive_stance');
    if (ds) victim_dmg_mod = 1.0 - ds.potency;
    else if (os) victim_dmg_mod = 1.5 - os.potency;

    // apply combined instigator & victim damage mods
    return 1 + (instigator_dmg_mod - 1) + (victim_dmg_mod - 1);
  },
};
module.exports = BattleCalc;
